import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Environment, EnvironmentService } from '@galaxy/core';
import { TranslateService } from '@ngx-translate/core';
import {
  DirectSyncSource,
  KeywordInfo,
  Location,
  ListingProductsApiService,
  SEODataSummary,
} from '@vendasta/listing-products';
import { EditionUpgradeAction } from '@vendasta/marketplace-apps';
import { RetailPrice } from '@vendasta/marketplace-packages';
import { UpgradeCTADialogComponent, UpgradeCTADialogConfig, VaProductNavActionButton } from '@vendasta/uikit';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { filter, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { AGIDTOKEN, APP_ID, DEMO_PAID_EDITION, KeywordStore, PROD_PAID_EDITION, SEOTableData } from '../../local-seo';
import {
  UpgradeCTADialogService,
  appID,
  UpgradeRedirectItem,
} from '../../upgrade-cta-dialog/upgrade-cta-dialog.service';
import {
  KeywordDialogResult,
  LocalSeoKeywordDialogComponent,
} from '../local-seo-keyword-dialog/local-seo-keyword-dialog.component';
import { KeywordTrackingService, SeoDataKeywordMetrics } from './keyword-tracking.service';

export interface Stat {
  value: string;
  rawValue: number;
}

interface ColumnConfigValue {
  name: string;
  class: string;
  iconURL?: string;
}

export interface ColumnConfig {
  [key: string]: ColumnConfigValue;
}

interface SortSettings {
  active: keyof SEOTableData;
  direction: 'asc' | 'desc';
}

export const SEODATA_TAB_GRAPH = 'graph';
export const SEODATA_TAB_GRID = 'grid';

@Component({
  selector: 'local-seo-keyword-tracking',
  templateUrl: './keyword-tracking.component.html',
  styleUrls: ['./keyword-tracking.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*', minHeight: '60px' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
    trigger('rotateIcon', [
      state('down', style({ transform: 'rotate(0)' })),
      state('up', style({ transform: 'rotate(-180deg)' })),
      transition('down <=> up', animate('225ms ease-out')),
    ]),
  ],
  standalone: false,
})
export class KeywordTrackingComponent implements OnInit, OnDestroy {
  actionButton: VaProductNavActionButton;
  SEOData$: Observable<SEOTableData[]>;
  Location$: Observable<Location>;
  loading$: Observable<boolean>;
  isKeywordListEmpty$: Observable<boolean>;
  Math: Math;
  subscriptions: Subscription[] = [];
  activeTab: string;
  ListingSyncService: boolean;
  gbpData$: Observable<DirectSyncSource>;
  keywordMetrics$: Observable<SeoDataKeywordMetrics[]>;
  columnConfig: ColumnConfig = {
    keyword: {
      name: 'LOCAL_SEO.KEYWORD',
      class: 'keyword',
    },
    local: {
      name: 'LOCAL_SEO.LOCAL_SEO.MAPS',
      class: 'divided',
      iconURL: 'https://vstatic-prod.apigateway.co/listing-builder-client/assets/images/google/google-maps.svg',
    },
    organic: {
      name: 'LOCAL_SEO.LOCAL_SEO.ORGANIC',
      class: 'divided',
      iconURL: 'https://vstatic-prod.apigateway.co/listing-builder-client/assets/images/google/google.png',
    },
    localRank: {
      name: 'LOCAL_SEO.LOCAL_SEO.RANK',
      class: 'divided',
    },
    organicRank: {
      name: 'LOCAL_SEO.LOCAL_SEO.RANK',
      class: 'divided',
    },
    localStartedAt: {
      name: 'LOCAL_SEO.LOCAL_SEO.STARTED_AT',
      class: '',
    },
    organicStartedAt: {
      name: 'LOCAL_SEO.LOCAL_SEO.STARTED_AT',
      class: '',
    },
    difficulty: {
      name: 'LOCAL_SEO.LOCAL_SEO.RANK_DIFFICULTY',
      class: 'divided',
    },
    searchVolume: {
      name: 'LOCAL_SEO.LOCAL_SEO.SEARCH_VOLUME',
      class: 'mat-header-wrap-text',
    },
    empty: {
      name: '',
      class: '',
    },
    empty2: {
      name: '',
      class: '',
    },
  };
  sortSettings = <SortSettings>{ active: 'localRank', direction: 'asc' };

  ExampleData = [
    {
      keyword: 'LOCAL_SEO.LOCAL_SEO.EXAMPLE_KEYWORD',
      mapsRank: 1,
      mapsStartedAt: 3,
      organicRank: 2,
      organicStartedAt: 10,
      rankDifficulty: 200,
      searchVolume: 4000,
    },
  ];

  statColumns: string[] = [
    'keyword',
    'localRank',
    'localStartedAt',
    'organicRank',
    'organicStartedAt',
    'difficulty',
    'searchVolume',
  ];
  columns: string[] = [
    'keyword',
    'localRank',
    'localStartedAt',
    'organicRank',
    'organicStartedAt',
    'difficulty',
    'searchVolume',
    'expandIcon',
  ];

  dialogRef: MatDialogRef<LocalSeoKeywordDialogComponent>;
  upgradeDialogRef: MatDialogRef<UpgradeCTADialogComponent>;
  listingProfileRedirectLink: string;
  canEditKeywords: boolean;
  editionUpgradeHidden$: Observable<boolean>;
  emptyStateImage$: Observable<string>;
  isLbFreeEdition$: Observable<boolean>;
  listingSyncPageLink: string;

  readonly gmbVerifyUrl = 'https://support.google.com/business/answer/7107242';

  constructor(
    public localSEOService: KeywordTrackingService,
    @Inject(AGIDTOKEN) public agid$: Observable<string>,
    private dialog: MatDialog,
    private upgradeDialogService: UpgradeCTADialogService,
    private translate: TranslateService,
    private router: Router,
    private route: ActivatedRoute,
    private env: EnvironmentService,
    private listingProductsService: ListingProductsApiService,
  ) {
    this.gbpData$ = localSEOService.gbpData$;
    this.isLbFreeEdition$ = localSEOService.isLBFreeEdition$;

    this.editionUpgradeHidden$ = combineLatest([
      this.isLbFreeEdition$,
      this.upgradeDialogService.getEditionChangeDetails$(),
    ]).pipe(
      map(([isFreeEdition, details]) => {
        return isFreeEdition && !!details?.hideUpgradeCta;
      }),
    );
    this.listingProfileRedirectLink = this.router
      .createUrlTree(['../listing-profile'], { relativeTo: this.route })
      .toString();
    this.loading$ = localSEOService.loading$$.asObservable();

    this.emptyStateImage$ = this.isLbFreeEdition$.pipe(
      map((isFreeEdition) =>
        isFreeEdition
          ? 'https://vstatic-prod.apigateway.co/listing-builder-client/assets/images/keywords-empty-state-std-edition.svg'
          : 'https://vstatic-prod.apigateway.co/listing-builder-client/assets/images/keywords-empty-state-pro-edition.svg',
      ),
    );
    this.keywordMetrics$ = localSEOService.keywordMetrics$.pipe(
      map((data) => structuredClone(data)), // Deep cloning
    );
    this.SEOData$ = combineLatest([
      localSEOService.SEOSummaryData$,
      localSEOService.keywordStore$,
      localSEOService.keywordMetrics$,
    ]).pipe(
      map(([seoData, keywordStore, metrics]: [SEODataSummary[], KeywordStore, SeoDataKeywordMetrics[]]) => {
        if (!keywordStore?.keywords?.length) {
          return [];
        }

        return keywordStore.keywords.map((keyword) => {
          const currentKMetric = metrics?.find((m) => m.keyword === keyword);
          const currentData = seoData?.find((d) => d.keyword === keyword);

          return new SEOTableData(keyword, currentData, currentKMetric, keywordStore.favoriteKeywords);
        });
      }),
    );
    this.Location$ = localSEOService.listingProfile$$.asObservable().pipe(map((profile) => profile?.napData));

    this.isKeywordListEmpty$ = this.localSEOService.keywordStore$.pipe(map((k) => k?.keywords?.length < 1));
    this.localSEOService.canEditKeywords$.subscribe((value) => {
      this.canEditKeywords = value;
    });
  }

  ngOnInit(): void {
    this.actionButton = {
      label: 'local-seo-title-action',
    } as VaProductNavActionButton;
  }

  public getValuesForSorting(data: SEOTableData, sortHeaderId: keyof SEOTableData): string | number {
    const value = data[sortHeaderId];
    switch (sortHeaderId) {
      case 'keyword':
        return (value as Stat).value.toLocaleLowerCase();
      default:
        return (value as Stat).rawValue ?? 999999; //set undefined as the highest possible value so it remains at the bottom
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  setSortSettings($event: SortSettings): void {
    this.sortSettings = { ...$event };
  }

  open(limit: number, suggestedKeywords: KeywordInfo[]): void {
    this.dialogRef = this.dialog.open(LocalSeoKeywordDialogComponent, {
      data: {
        keywords: [],
        keywordLimit: limit,
        suggestedKeywords: suggestedKeywords,
        canEditKeywords: this.canEditKeywords,
      },
      width: '600px',
    });
    this.subscriptions.push(
      this.dialogRef.afterClosed().subscribe((result: KeywordDialogResult) => {
        if (result !== undefined) {
          this.localSEOService.updateKeywords(result.originalKeywords, result.newKeywords);
        }
      }),
    );
  }

  OpenUpgradeDialog() {
    const editionChangeDetails$ = this.upgradeDialogService.getEditionChangeDetails$();
    const upgradePrice$: Observable<RetailPrice> = combineLatest([
      this.upgradeDialogService.partnerMarket$,
      editionChangeDetails$,
    ]).pipe(
      switchMap(([pmConfig, editionChangeDetails]) => {
        let paidEdition = DEMO_PAID_EDITION;
        if (this.env.getEnvironment() == Environment.PROD) {
          paidEdition = PROD_PAID_EDITION;
        }
        return this.upgradeDialogService.getUpgradePrice$(pmConfig, appID, editionChangeDetails, paidEdition);
      }),
      shareReplay(1),
    );
    const formattedUpgradePrice$ = upgradePrice$.pipe(
      switchMap((upgradePrice) => this.upgradeDialogService.getFormattedRetailPrice$(upgradePrice)),
    );
    combineLatest([
      upgradePrice$,
      formattedUpgradePrice$,
      editionChangeDetails$,
      this.upgradeDialogService.getCustomUpgradeModalContent$(),
      this.upgradeDialogService.appSettings$,
    ])
      .pipe(
        take(1),
        switchMap(
          ([upgradePrice, formattedUpgradePrice, editionChangeDetails, customUpgradeModalContent, settings]) => {
            const marketingPoints = [
              this.translate.instant('EDITION.UPGRADE_MODAL.MARKETING_POINTS.LINE_1'),
              this.translate.instant('EDITION.UPGRADE_MODAL.MARKETING_POINTS.LINE_2'),
              this.translate.instant('EDITION.UPGRADE_MODAL.MARKETING_POINTS.LINE_3'),
              this.translate.instant('EDITION.UPGRADE_MODAL.MARKETING_POINTS.LINE_4'),
              this.translate.instant('EDITION.UPGRADE_MODAL.MARKETING_POINTS.LINE_5'),
              this.translate.instant('EDITION.UPGRADE_MODAL.MARKETING_POINTS.LINE_6'),
            ];
            const appName =
              settings.branding &&
              settings.branding.enabled &&
              settings.branding.enabled === true &&
              settings.branding.name
                ? settings.branding.name
                : 'Local SEO';

            let ctaText =
              !!customUpgradeModalContent && customUpgradeModalContent.buttonText
                ? customUpgradeModalContent.buttonText
                : undefined;
            if (editionChangeDetails?.editionUpgradeAction && !ctaText) {
              switch (editionChangeDetails?.editionUpgradeAction) {
                case EditionUpgradeAction.EDITION_UPGRADE_ACTION_CONTACT_SALES:
                  ctaText = this.translate.instant('UPGRADE_DIALOG.PRICE.CONTACT_SALES');
                  break;
                default:
                  ctaText = this.translate.instant('LISTING_DISTRIBUTION.UPGRADE_MODAL.BUTTON_TEXT');
                  break;
              }
            }

            const dialogConfig: UpgradeCTADialogConfig = {
              editionName: `${appName} ${this.translate.instant('COMMON.PRO')}`,
              imageText:
                !!customUpgradeModalContent && customUpgradeModalContent.titleText
                  ? customUpgradeModalContent.titleText
                  : this.translate.instant('EDITION.UPGRADE_MODAL.IMAGE_TEXT'),
              imageUrl:
                !!customUpgradeModalContent && customUpgradeModalContent.imageUrl
                  ? customUpgradeModalContent.imageUrl
                  : `https://vstatic-prod.apigateway.co/listing-builder-client/assets/images/LocalSearch.svg`,
              keySellingPoints:
                !!customUpgradeModalContent && customUpgradeModalContent.sellingPoints
                  ? customUpgradeModalContent.sellingPoints
                  : marketingPoints,
              ctaText: ctaText || this.translate.instant('LISTING_DISTRIBUTION.UPGRADE_MODAL.BUTTON_TEXT'),
              upgradeNowText: this.upgradeDialogService.getUpgradeText(upgradePrice, editionChangeDetails),
              price: formattedUpgradePrice,
              frequency: this.upgradeDialogService.getFrequencyText(upgradePrice),
            };

            this.upgradeDialogRef = this.dialog.open(UpgradeCTADialogComponent, {
              data: dialogConfig,
            });
            return this.upgradeDialogRef.afterClosed().pipe(
              filter((upgradeClicked) => !!upgradeClicked),
              switchMap(() => {
                const isCustomPackage =
                  editionChangeDetails.editionUpgradeAction == EditionUpgradeAction.EDITION_UPGRADE_ACTION_CUSTOM_ORDER;
                let paidEdition = DEMO_PAID_EDITION;
                if (this.env.getEnvironment() == Environment.PROD) {
                  paidEdition = PROD_PAID_EDITION;
                }
                const item: UpgradeRedirectItem = isCustomPackage
                  ? { packageId: editionChangeDetails.priceReferencePackageId }
                  : { appId: APP_ID, editionId: paidEdition };
                return this.upgradeDialogService.buildBusinessCenterUpgradeRedirectInfo(
                  item,
                  editionChangeDetails.editionUpgradeAction,
                );
              }),
            );
          },
        ),
      )
      .subscribe((redirectInfo) => {
        this.upgradeDialogService.sendToVbc(redirectInfo);
      });
  }

  deleteKeyword(keyword: string): void {
    this.localSEOService.deleteKeyword(keyword);
  }
}
